"use client";

import { createContext, useEffect, useMemo } from "react";
import type { ReactNode } from "react";

type CustomI18nContextType = {
  translations: Record<string, string>;
  ns: string;
  locale: string;
};

export const CustomI18nContext = createContext<CustomI18nContextType | null>(null);

/**
 * Persists the locale override that brought the visitor here (embed config /
 * ?locale= / email link) into the cal-locale cookie, so later page visits
 * without the query param (reschedule/cancel links that predate param
 * forwarding, email links opened in another tab, etc.) still resolve to the
 * same language via getLocale()'s cookie fallback. SameSite=None so the cookie
 * also sticks when cal.diy runs inside a cross-site embed iframe (Chrome
 * rejects Lax cookies set in third-party contexts; Safari blocks all
 * third-party cookies, where this fallback is a no-op). The value is
 * non-sensitive; downstream getLocale() re-validates it against the supported
 * locale list before use.
 */
function LocaleCookieSetter({ locale }: { locale: string }) {
  useEffect(() => {
    document.cookie = `cal-locale=${encodeURIComponent(locale)}; path=/; max-age=31536000; SameSite=None; Secure`;
  }, [locale]);
  return null;
}

export function CustomI18nProvider({
  children,
  translations,
  locale,
  ns,
}: CustomI18nContextType & {
  children: ReactNode;
}) {
  // Memoize the value to prevent re-renders unless the data changes
  const value = useMemo(
    () => ({
      translations,
      locale,
      ns,
    }),
    [locale, ns]
  );

  return (
    <CustomI18nContext.Provider value={value}>
      <LocaleCookieSetter locale={locale} />
      {children}
    </CustomI18nContext.Provider>
  );
}
